/**
 * Close all open nav items
 */
const closeAllOpenItems = () => {
  const menuItems = document.querySelectorAll('[data-module="main-nav-item"]');
  const menuBtns = document.querySelectorAll('.site-nav__btn');
  const mobileMenu = document.querySelector('#mobile-menu-toggle');
  const mobileMenuBtn = document.querySelector('[data-element="mobile-menu-btn"]');

  menuItems.forEach((item) => {
    item.classList.remove('is-open');
  });

  menuBtns.forEach((btn) => {
    btn.setAttribute('aria-expanded', false);
  });

  if (mobileMenuBtn && mobileMenu && mobileMenu.dataset.disableMobileDropdown) {
    MobileMenuBtn.closeMobileMenu(mobileMenuBtn);
  }
};

/**
 * Are any nav items open
 * @return {Boolean}
 */
const isNavOpen = () => {
  const menuItems = [...document.querySelectorAll('[data-module="main-nav-item"]')];

  const open = menuItems.filter((item) => {
    return item.classList.contains('is-open');
  });

  return (open.length > 0) ? true : false;
};

/**
 * Are any nav items active
 * @return {Boolean}
 */
const isNavActive = () => {
  const menuItems = [...document.querySelectorAll('[data-module="main-nav-item"]')];

  const active = menuItems.filter((item) => {
    return item.classList.contains('is-active');
  });

  return (active.length > 0) ? true : false;
};

/**
 * Is the viewport on mobile viewport
 * @return {Boolean}
 */
const isMobileViewport = () => {
  const width = 800;
  const widthMatch = window.matchMedia(`(max-width: ${width}px)`);

  return widthMatch.matches;
};

class MainNavItem {
  constructor(element) {
    this.timer;
    this.menuItem = element;
    this.menuBtn = element.querySelector('.site-nav__btn');
    this.subNav = element.querySelector('.sub-nav');
    this.header = document.querySelector('[role="banner"]');
  }

  init() {
    // Prebind functions for cleanup later
    this.onMouseover = this.onMouseover.bind(this);
    this.onMouseout = this.onMouseout.bind(this);
    this.onBtnClick = this.onBtnClick.bind(this);

    // Bind listeners that are dependent on screen size
    this.bindMatchMedia();

    // on touch screens these click events might need something to make it work on first tap
    this.menuBtn.addEventListener('click', this.onBtnClick);

    // inital state of subnav is hidden
    this.subNav.hidden = true;

    if (isNavActive() && isMobileViewport()) {
      this.subNav.hidden = false;
    }
  }

  /**
   * Bind Event Listeners according to media query
   */
  bindMatchMedia() {
    const width = 800;
    const widthMatch = window.matchMedia(`(min-width: ${width}px)`);

    widthMatch.addEventListener('change', (matchMedia) => {
      // If it matches then we are large screen
      if (matchMedia.matches) {
        closeAllOpenItems();
        this.menuItem.addEventListener('mouseover', this.onMouseover);
        this.menuItem.addEventListener('mouseout', this.onMouseout);
      } else {
        // remove mouse listeners because subnav expansion is triggered by click
        closeAllOpenItems();
        this.menuItem.removeEventListener('mouseover', this.onMouseover);
        this.menuItem.removeEventListener('mouseout', this.onMouseout);
      }
    });

    // Setup listeners for page load if needed
    if (window.innerWidth >= width) {
      this.menuItem.addEventListener('mouseover', this.onMouseover);
      this.menuItem.addEventListener('mouseout', this.onMouseout);
    }
  }

  /**
   * @param {nodelist} elements collection of focusable HTMLElements
   * @param {number} previous index of previously focused item
   * @param {number} current index of newly focused item
   **/
  setFocus(elements, previous, current) {
    elements[previous].tabIndex = -1;

    elements[current].tabIndex = 0;
    elements[current].focus();
  }

  /**
   * @param {number} previous previously focused item, either first or last
   * @param {number} current newly focused item, either first or last
   **/
  restartFocus(previous, current) {
    previous.tabIndex = -1;
    current.tabIndex = 0;
    current.focus();
  }

  /**
   * Keyboard focus management for the subnav
   * @see https://www.w3.org/TR/wai-aria-practices/#kbd_roving_tabindex
   */
  trapFocus() {
    const focusableEls = this.subNav.querySelectorAll('a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])');
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];
    const KEYCODE_TAB = 9;
    const KEYCODE_ESC = 27;

    firstFocusableEl.tabIndex = 0;
    firstFocusableEl.focus();

    let index = 0;

    const subNavKeydown = (event) => {
      // Don't bubble events because document has keydown listener
      // that closes the menu if all submenus are closed.
      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'Escape' || event.keyCode === KEYCODE_ESC) {
        closeAllOpenItems();
        this.closeSubNav();

        this.menuBtn.focus();
        this.subNav.removeEventListener('keydown', subNavKeydown);
      }

      if (event.key === 'Tab' || event.keyCode === KEYCODE_TAB) {
        // shift + tab
        if (event.shiftKey) {
          // If on first item moving to previous
          if (index === 0) {
            index = focusableEls.length - 1;
            this.restartFocus(firstFocusableEl, lastFocusableEl);
          } else {
            index -= 1;
            this.setFocus(focusableEls, (index + 1), index);
          }
        } else {
          // If on last item moving to next
          if (index === focusableEls.length - 1) {
            index = 0;
            this.restartFocus(lastFocusableEl, firstFocusableEl);
          } else {
            index += 1;
            this.setFocus(focusableEls, (index - 1), index);
          }
        }
      }
    };

    this.subNav.addEventListener('keydown', subNavKeydown);
  }

  showSubNav() {
    this.menuItem.classList.add('is-open');
    this.menuBtn.setAttribute('aria-expanded', true);
    this.subNav.hidden = false;

    // Only add if nav items are open
    if (isNavOpen()) {
      this.header.classList.add('is-expanded');
    }
  }

  closeSubNav() {
    this.menuBtn.setAttribute('aria-expanded', false);
    this.subNav.hidden = true;
    this.menuItem.classList.remove('is-open');

    // Only remove if no nav items are open
    if (!isNavOpen()) {
      this.header.classList.remove('is-expanded');
    }
  }

  onBtnClick() {
    if (this.menuItem.classList.contains('is-open')) {
      this.closeSubNav();
    } else {
      closeAllOpenItems();
      this.showSubNav();
      this.trapFocus();
    }
  }

  onMouseover() {
    this.header.classList.add('is-expanded');
    closeAllOpenItems();
    this.showSubNav();
    clearTimeout(this.timer);
  }

  onMouseout() {
    this.timer = setTimeout(() => {
      this.closeSubNav();

      // Only remove if no nav items are open
      if (!isNavOpen()) {
        this.header.classList.remove('is-expanded');
      }
    }, 375);
  }
}

class MobileMenuBtn {
  constructor() {
    this.mobileMenuBtn = document.querySelector('[data-element="mobile-menu-btn"]');
    this.mainNav = document.querySelector('[role="navigation"]');
  }

  init() {
    this.onMenuToggle = this.onMenuToggle.bind(this);
    this.onMenuToggleClick = this.onMenuToggleClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.mobileMenuBtn.addEventListener('keyup', this.onMenuToggle);
    this.mobileMenuBtn.addEventListener('click', this.onMenuToggleClick);

    this.bindMatchMedia();
  }

  bindMatchMedia() {
    const width = 800;
    const widthMatch = window.matchMedia(`(min-width: ${width}px)`);

    widthMatch.addEventListener('change', (matchMedia) => {
      // If it matches then we are large screen
      if (matchMedia.matches) {
        // Close mobile menu if it is open
        MobileMenuBtn.closeMobileMenu(this.mobileMenuBtn);
        this.mainNav.hidden = false;
      } else {
        this.mainNav.hidden = true;
      }
    });
  }

  // js enhancement for "return" key on menu toggle
  onMenuToggle(event) {
    const KEYCODE_ENTER = 13;

    if (event.key === 'Enter' || event.keyCode === KEYCODE_ENTER) {
      this.mobileMenuBtn.click();
    }
  }

  static closeMobileMenu(mobileMenuBtn) {
    mobileMenuBtn.checked = false;
    document.documentElement.classList.remove('has-menu');
    document.removeEventListener('keydown', this.onKeyDown);

    mobileMenuBtn.focus();
  }

  /**
   * Keydown on document for closing mobile menu
   * @param {event} event the keydown event
   */
  onKeyDown(event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      MobileMenuBtn.closeMobileMenu(this.mobileMenuBtn);
    }
  }

  onMenuToggleClick() {
    if (this.mobileMenuBtn.checked) {
      document.documentElement.classList.add('has-menu');
      this.mainNav.hidden = false;
      document.addEventListener('keydown', this.onKeyDown);
    } else {
      closeAllOpenItems();
      document.documentElement.classList.remove('has-menu');
      document.removeEventListener('keydown', this.onKeyDown);
    }
  }
}


const navigation = {
  init() {
    const navItems = document.querySelectorAll('[data-module="main-nav-item"]');

    if (navItems && navItems.length > 0) {
      navItems.forEach((item) => {
        const mainNavItem = new MainNavItem(item);
        mainNavItem.init();
      });
    }

    const mobileMenuBtn = document.querySelector('[data-element="mobile-menu-btn"]');

    if (mobileMenuBtn) {
      const mobileMenu = new MobileMenuBtn();
      mobileMenu.init();
    }
  },

  isMobileViewport() {
    return isMobileViewport();
  },

  closeMobileMenu(element) {
    const mobileMenuBtn = document.querySelector('[data-element="mobile-menu-btn"]');

    if (mobileMenuBtn) {
      MobileMenuBtn.closeMobileMenu(element);
    }
  },
};

export default navigation;
